/**
 * TanStack Query hooks for the admin categories surface (P1 Tier-A).
 *
 * PIM Wave P1 Task 6 adds: `useCategoryAliases`/`useCreateCategoryAlias`/
 * `useDeleteCategoryAlias` (nested under `categoryKeys.aliases(categoryId)`,
 * invalidating ONLY that key — brief §1, mirrors the `attributeFieldKeys.
 * options` idiom except aliases do NOT also invalidate the categories list,
 * since aliases carry no state the category rows render) and
 * `useArchiveCategory`/`useUnarchiveCategory` (invalidate the categories
 * family like every other category mutation).
 *
 * PIM Wave P1 Task 7 adds `useMergeCategory` — invalidates the WHOLE
 * categories family (the tree must refresh: source archived, children
 * reparented under target) AND both the source's and target's alias keys
 * (aliases carry over per the merge contract, brief §1). Success does NOT
 * need optimistic UI (brief §1) — the caller renders the returned counts.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  archiveCategory,
  categoryKeys,
  createCategory,
  createCategoryAlias,
  deleteCategory,
  deleteCategoryAlias,
  listAllAdminCategories,
  listCategories,
  listCategoryAliases,
  mergeCategory,
  patchCategory,
  unarchiveCategory,
  updateCategory,
  type AdminCategory,
  type AdminCategoryCreate,
  type AdminCategoryListResponse,
  type AdminCategoryPatch,
  type AdminCategoryUpdate,
  type CategoryAlias,
  type CategoryAliasCreateBody,
  type CategoryAliasListResponse,
  type CategoryMergeRequestBody,
  type CategoryMergeResponse,
} from "@/features/admin/categories/api";

const PAGE_LIMIT = 100;

/**
 * Full flat category list (drains the cursor), used by the tree view (P1
 * Task 5 — there is no admin tree endpoint, recon C4). Same
 * `["admin","categories",...]` key family as `categoryKeys.list` — the
 * existing `useInvalidateCategories()` prefix-invalidate below covers this
 * query too, no changes needed there.
 */
export function useAllAdminCategories(): UseQueryResult<AdminCategory[], Error> {
  return useQuery<AdminCategory[], Error>({
    queryKey: categoryKeys.all(),
    queryFn: () => listAllAdminCategories(),
    staleTime: 10_000,
  });
}

export function useCategories(): UseInfiniteQueryResult<
  InfiniteData<AdminCategoryListResponse>,
  Error
> {
  return useInfiniteQuery<
    AdminCategoryListResponse,
    Error,
    InfiniteData<AdminCategoryListResponse>,
    ReturnType<typeof categoryKeys.list>,
    string | null
  >({
    queryKey: categoryKeys.list({}),
    queryFn: ({ pageParam }) => listCategories({ cursor: pageParam, limit: PAGE_LIMIT }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    staleTime: 10_000,
  });
}

function useInvalidateCategories(): () => Promise<void> {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["admin", "categories"] });
}

export function useCreateCategory(): UseMutationResult<AdminCategory, Error, AdminCategoryCreate> {
  const invalidate = useInvalidateCategories();
  return useMutation<AdminCategory, Error, AdminCategoryCreate>({
    mutationFn: (body) => createCategory(body),
    onSuccess: () => invalidate(),
  });
}
export function useUpdateCategory(): UseMutationResult<
  AdminCategory,
  Error,
  { id: string; body: AdminCategoryUpdate }
> {
  const invalidate = useInvalidateCategories();
  return useMutation<AdminCategory, Error, { id: string; body: AdminCategoryUpdate }>({
    mutationFn: ({ id, body }) => updateCategory(id, body),
    onSuccess: () => invalidate(),
  });
}
export function useDeleteCategory(): UseMutationResult<AdminCategory, Error, string> {
  const invalidate = useInvalidateCategories();
  return useMutation<AdminCategory, Error, string>({
    mutationFn: (id) => deleteCategory(id),
    onSuccess: () => invalidate(),
  });
}

/**
 * Full-shape partial PATCH (P1 Task 5). Backs the tree's sibling-reorder
 * drag (per-category `{ sort_order }` patches — no batch reorder route for
 * categories, recon C5) — the diff/reindex/`Promise.all`/optimistic-order/
 * rollback orchestration mirrors `OptionsEditor.tsx`'s `handleDragEnd` and
 * lives in `CategoryListPage.tsx`, not here (this hook only exposes the
 * single-item mutation, same division of labor as
 * `useUpdateAttributeFieldOption`).
 */
export function usePatchCategory(): UseMutationResult<
  AdminCategory,
  Error,
  { id: string; body: AdminCategoryPatch }
> {
  const invalidate = useInvalidateCategories();
  return useMutation<AdminCategory, Error, { id: string; body: AdminCategoryPatch }>({
    mutationFn: ({ id, body }) => patchCategory(id, body),
    onSuccess: () => invalidate(),
  });
}

/** Archive/unarchive (P1 Task 6) — invalidate the categories family like
 * every other category mutation (the row's `is_archived` badge/actions
 * live off that same list). */
export function useArchiveCategory(): UseMutationResult<AdminCategory, Error, string> {
  const invalidate = useInvalidateCategories();
  return useMutation<AdminCategory, Error, string>({
    mutationFn: (id) => archiveCategory(id),
    onSuccess: () => invalidate(),
  });
}
export function useUnarchiveCategory(): UseMutationResult<AdminCategory, Error, string> {
  const invalidate = useInvalidateCategories();
  return useMutation<AdminCategory, Error, string>({
    mutationFn: (id) => unarchiveCategory(id),
    onSuccess: () => invalidate(),
  });
}

// ---------------------------------------------------------------------
// Category aliases (P1 Task 6) — nested under a category id. Mutations
// invalidate ONLY `categoryKeys.aliases(categoryId)` (brief §1) — never the
// categories list/detail family.
// ---------------------------------------------------------------------

export function useCategoryAliases(
  categoryId: string | null,
): UseQueryResult<CategoryAliasListResponse, Error> {
  return useQuery<CategoryAliasListResponse, Error>({
    queryKey: categoryKeys.aliases(categoryId ?? ""),
    queryFn: () => listCategoryAliases(categoryId!),
    enabled: categoryId !== null,
    staleTime: 10_000,
  });
}

function useInvalidateCategoryAliases(): (categoryId: string) => Promise<void> {
  const qc = useQueryClient();
  return (categoryId: string) =>
    qc.invalidateQueries({ queryKey: categoryKeys.aliases(categoryId) });
}

export function useCreateCategoryAlias(): UseMutationResult<
  CategoryAlias,
  Error,
  { categoryId: string; body: CategoryAliasCreateBody }
> {
  const invalidate = useInvalidateCategoryAliases();
  return useMutation<CategoryAlias, Error, { categoryId: string; body: CategoryAliasCreateBody }>({
    mutationFn: ({ categoryId, body }) => createCategoryAlias(categoryId, body),
    onSuccess: (_data, { categoryId }) => invalidate(categoryId),
  });
}

export function useDeleteCategoryAlias(): UseMutationResult<
  void,
  Error,
  { categoryId: string; aliasId: string }
> {
  const invalidate = useInvalidateCategoryAliases();
  return useMutation<void, Error, { categoryId: string; aliasId: string }>({
    mutationFn: ({ categoryId, aliasId }) => deleteCategoryAlias(categoryId, aliasId),
    onSuccess: (_data, { categoryId }) => invalidate(categoryId),
  });
}

// ---------------------------------------------------------------------
// Category merge (P1 Task 7) — commits immediately (USER RULING C2, no
// dry-run). Invalidates the whole categories family (tree refresh: source
// archived, children reparented) AND both endpoints' alias keys (aliases
// carry over to the target).
// ---------------------------------------------------------------------

export function useMergeCategory(): UseMutationResult<
  CategoryMergeResponse,
  Error,
  { sourceId: string; body: CategoryMergeRequestBody }
> {
  const invalidate = useInvalidateCategories();
  const invalidateAliases = useInvalidateCategoryAliases();
  return useMutation<CategoryMergeResponse, Error, { sourceId: string; body: CategoryMergeRequestBody }>({
    mutationFn: ({ sourceId, body }) => mergeCategory(sourceId, body),
    onSuccess: async (_data, { sourceId, body }) => {
      await invalidate();
      await invalidateAliases(sourceId);
      await invalidateAliases(body.target_category_id);
    },
  });
}
